import { DateTime } from 'luxon';
import { toNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/calloutMappers';

export interface TotalDailyOvertimeHoursResponse {
  current_period_overtime: number | string;
  period_days: number;
  change_prct: number | string | null;
}

export interface DailyOvertimeRateResponse {
  current_period_overtime_rate: number | string;
  period_days: number;
  change_prct: number | string | null;
}

export interface DailyOvertimeByPositionResponseItem {
  position_id: number;
  position_name: string;
  total_overtime_hours: number | string;
  overtime_percentage: number | string;
}

export interface DailyOvertimeByPositionResponse {
  daily_overtime_by_position: DailyOvertimeByPositionResponseItem[];
  meta?: DashboardPaginationMeta;
}

export interface DailyOvertimeByEmploymentStatusResponseItem {
  employment_status: string;
  total_overtime_hours: number | string;
  overtime_percentage: number | string;
}

export interface DailyOvertimeByEmploymentStatusResponse {
  daily_overtime_by_employment_status: DailyOvertimeByEmploymentStatusResponseItem[];
}

export interface TotalWeeklyOvertimeHoursResponse {
  current_period_total_overtime: number | string;
  current_pay_period_start_date: string;
  current_pay_period_end_date: string;
  change_prct: number | string | null;
}

export interface WeeklyOvertimeRateResponse {
  current_period_overtime_rate: number | string;
  current_pay_period_start_date: string;
  current_pay_period_end_date: string;
  change_prct: number | string | null;
}

export interface WeeklyOvertimeByPositionResponseItem {
  position_id: number;
  position_name: string;
  total_overtime_hours: number | string;
  overtime_percentage: number | string;
}

export interface WeeklyOvertimeByPositionResponse {
  weekly_overtime_by_position: WeeklyOvertimeByPositionResponseItem[];
  meta?: DashboardPaginationMeta;
}

export interface WeeklyOvertimeByEmploymentStatusResponseItem {
  employment_status: string;
  total_overtime_hours: number | string;
  overtime_percentage: number | string;
}

export interface WeeklyOvertimeByEmploymentStatusResponse {
  weekly_overtime_by_employment_status: WeeklyOvertimeByEmploymentStatusResponseItem[];
}

export interface DailyOvertimeDetailRowResponseItem {
  day: string;
  jane_employee_number: string | null;
  jane_name: string;
  jane_employment_status_label: string | null;
  jane_primary_community_id: number;
  jane_primary_community_name: string;
  jane_primary_position_id: number;
  jane_primary_position_name: string;
  daily_limit: number | string;
  total_paid_hours: number | string;
  total_overtime_hours: number | string;
}

export interface DashboardPaginationMeta {
  current_page: number;
  next_page: number | null;
  prev_page: number | null;
  total_pages: number;
  total_count: number;
}

export interface DailyOvertimeDriverRowResponseItem {
  jane_employee_number: string | null;
  jane_name: string;
  jane_employment_status_label: string | null;
  jane_primary_community_id: number;
  jane_primary_community_name: string;
  jane_primary_position_id: number;
  jane_primary_position_name: string;
  daily_limit: number | string;
  total_paid_hours: number | string;
  total_overtime_hours: number | string;
}

export interface DailyOvertimeDetailsResponse {
  daily_overtime_details: DailyOvertimeDetailRowResponseItem[];
  meta: DashboardPaginationMeta;
}

export interface DailyOvertimeDriversResponse {
  daily_overtime_drivers: DailyOvertimeDriverRowResponseItem[];
  meta: DashboardPaginationMeta;
}

export interface WeeklyOvertimeDetailRowResponseItem {
  pay_period_start_at: string;
  pay_period_end_at: string;
  jane_employee_number: string | null;
  jane_name: string;
  jane_employment_status_label: string | null;
  jane_primary_community_name: string;
  jane_primary_position_name: string;
  weekly_limit: number | string;
  total_paid_hours: number | string;
  total_overtime_hours: number | string;
}

export interface WeeklyOvertimeDetailsResponse {
  weekly_overtime_details: WeeklyOvertimeDetailRowResponseItem[];
  meta: DashboardPaginationMeta;
}

export interface WeeklyOvertimeDriverRowResponseItem {
  jane_employee_number: string | null;
  jane_name: string;
  jane_employment_status_label: string | null;
  jane_primary_community_name: string;
  jane_primary_position_name: string;
  weekly_limit: number | string;
  total_paid_hours: number | string;
  total_overtime_hours: number | string;
}

export interface WeeklyOvertimeDriversResponse {
  weekly_overtime_drivers: WeeklyOvertimeDriverRowResponseItem[];
  meta: DashboardPaginationMeta;
}

export interface OvertimeTrendBucketResponseItem {
  bucket_start: string;
  total_daily_overtime_hours: number | string;
}

export interface DailyOvertimeTrendResponse {
  daily_overtime_trend: {
    granularity: 'day' | 'week' | 'month';
    buckets: OvertimeTrendBucketResponseItem[];
  };
}

export interface WeeklyOvertimeTrendResponse {
  weekly_overtime_trend: OvertimeTrendBucketResponseItem[];
}

export function mapOvertimeTrendBuckets(
  buckets: OvertimeTrendBucketResponseItem[],
): { date: string; overtimeHours: number }[] {
  return buckets.map((bucket) => ({
    date: bucket.bucket_start.slice(0, 10),
    overtimeHours: toNumber(bucket.total_daily_overtime_hours),
  }));
}

// total_weekly_overtime_hours and weekly_overtime_rate require a single scheduling
// cohort (one primary community + one primary position) per the B2B v4 contract.
export function isSingleSchedulingCohort(
  locationIds: string[] | null,
  positions: string[] | null,
): boolean {
  return locationIds?.length === 1 && positions?.length === 1;
}

function toDateParam(isoDateTime: string): string {
  return isoDateTime.slice(0, 10);
}

function toExclusiveEndDateParam(isoDateTime: string): string {
  return DateTime.fromISO(isoDateTime).plus({ days: 1 }).toFormat('yyyy-MM-dd');
}

function buildNarrowingFilters(
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  const filters: Record<string, unknown> = {};

  if (locationIds && locationIds.length > 0) {
    filters.with_jane_primary_community = locationIds.map(Number);
  }
  if (positions && positions.length > 0) {
    filters.with_jane_primary_position = positions.map(Number);
  }
  if (employmentTypes.length > 0) {
    filters.for_employment_statuses = employmentTypes;
  }

  return filters;
}

export function buildDailyOvertimeQuery(
  dateRange: [string, string],
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  return {
    filters: {
      with_start_date: {
        begin: toDateParam(dateRange[0]),
        end: toDateParam(dateRange[1]),
      },
      ...buildNarrowingFilters(locationIds, positions, employmentTypes),
    },
  };
}

export function buildPayPeriodThroughRangeQuery(
  dateRange: [string, string],
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  return {
    filters: {
      with_pay_period_through_range: {
        begin: toDateParam(dateRange[0]),
        end: toExclusiveEndDateParam(dateRange[1]),
      },
      ...buildNarrowingFilters(locationIds, positions, employmentTypes),
    },
  };
}

export function buildSchedulingCohortQuery(
  dateRange: [string, string],
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  const filters: Record<string, unknown> = {
    for_pay_period: {
      begin: toDateParam(dateRange[0]),
      end: toExclusiveEndDateParam(dateRange[1]),
    },
    for_scheduling_cohort: {
      community_id: Number(locationIds?.[0]),
      position_id: Number(positions?.[0]),
    },
  };

  if (employmentTypes.length > 0) {
    filters.for_employment_statuses = employmentTypes;
  }

  return { filters };
}

export function buildWeeklyOvertimeTrendQuery(
  dateRange: [string, string],
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
  numberOfPeriods: number,
): Record<string, unknown> {
  return {
    number_of_periods: numberOfPeriods,
    ...buildSchedulingCohortQuery(dateRange, locationIds, positions, employmentTypes),
  };
}
